<template>
  <div class="checkout-wrapper">
    <div class="checkout-container">
      <router-link to="/cart" class="back-link">&larr; {{ translations.checkout.back_to_cart }}</router-link>

      <!-- PODSUMOWANIE (na mobile na górze, na desktopie sticky do samej krawędzi) -->
      <aside class="summary-section">
        <h3>{{ translations.checkout.summary }}</h3>
        <ul v-if="cart.length">
          <li v-for="item in cart" :key="item.id + '-' + (item.variantKey || '-')" class="cart-item">
            <div class="cart-img-container">
              <img :src="item.image" class="cart-img" />
            </div>
            <div>
              <strong>{{ item.name }}</strong>
              <div class="variant" v-if="item.variant">{{ item.variant.type?.name }}</div>
              <div>
                {{ Number(item.price).toFixed(2) }} {{ getCurrencySymbol(currencyCode) }} × {{ item.quantity }}
              </div>
            </div>
          </li>
        </ul>
        <div v-else class="empty">{{ translations.checkout.empty_cart }}</div>
        <hr />
        <div class="summary-row">
          {{ translations.checkout.delivery }}:
          <span>{{ shippingTotal.toFixed(2) }} {{ getCurrencySymbol(currencyCode) }}</span>
        </div>
        <div class="summary-row summary-note">
          {{ translations.checkout.shipping_carrier_gls }}
        </div>
        <div class="summary-total">
          {{ translations.checkout.total }}:
          <strong>{{ totalWithShipping.toFixed(2) }} {{ getCurrencySymbol(currencyCode) }}</strong>
        </div>
      </aside>

      <!-- FORMULARZ -->
      <form class="form-section" @submit.prevent="order">
        <h2>{{ translations.checkout.step_contact }}</h2>
        <div class="two-cols">
          <label>{{ translations.checkout.first_name }} <input type="text" v-model="form.firstName" required /></label>
          <label>{{ translations.checkout.last_name }} <input type="text" v-model="form.lastName" required /></label>
        </div>
        <div class="two-cols">
          <label>{{ translations.checkout.phone }} <input type="tel" v-model="form.phone" required /></label>
          <label>{{ translations.checkout.email }} <input type="email" v-model="form.email" required /></label>
        </div>

        <h2>{{ translations.checkout.step_address }}</h2>
        <div class="two-cols">
          <label>{{ translations.checkout.address }} <input type="text" v-model="form.address" required /></label>
          <label>{{ translations.checkout.city }} <input type="text" v-model="form.city" required /></label>
        </div>
        <div class="two-cols">
          <label>{{ translations.checkout.zip }} <input type="text" v-model="form.zip" required /></label>
          <label>
            {{ translations.checkout.country }}
            <select v-model="form.country">
              <option v-for="c in availableCountries" :key="c.code" :value="c.code">
                {{ c.name }}
              </option>
            </select>
          </label>
        </div>

        <label class="checkbox pretty-checkbox">
          <input type="checkbox" v-model="form.wantInvoice" />
          <span class="custom-check"></span>
          {{ translations.checkout.want_invoice }}
        </label>

        <div v-if="form.wantInvoice" class="invoice-section">
          <hr />
          <h3>{{ translations.checkout.step_invoice }}</h3>
          <p class="invoice-hint">{{ translations.checkout.invoice_hint }}</p>

          <label>{{ translations.checkout.invoice_company }}
            <input type="text" v-model="form.invoiceCompany" :required="form.wantInvoice" />
          </label>

          <label>{{ translations.checkout.invoice_nip }}
            <input type="text" v-model="form.invoiceNIP" :required="form.wantInvoice" />
          </label>

          <label>{{ translations.checkout.invoice_address }}
            <input type="text" v-model="form.invoiceAddress" />
          </label>

          <label>{{ translations.checkout.invoice_zip }}
            <input type="text" v-model="form.invoiceZip" />
          </label>

          <label>{{ translations.checkout.invoice_city }}
            <input type="text" v-model="form.invoiceCity" />
          </label>

          <label>{{ translations.checkout.invoice_country }}
            <select v-model="form.invoiceCountry">
              <option v-for="c in availableCountries" :key="c.code" :value="c.code">
                {{ c.name }}
              </option>
            </select>
          </label>
        </div>

        <!-- METODY PŁATNOŚCI -->
        <h2>{{ translations.checkout.step_payment }}</h2>
        <div class="payment-box-group">
          <label
            v-for="method in availablePaymentMethods"
            :key="method.id"
            class="payment-box"
            :class="{ selected: form.paymentMethod === method.id }"
          >
            <input type="radio" :value="method.id" v-model="form.paymentMethod" name="payment" />
            <div class="radio-content">
              <span class="dot"></span>
              <span class="method-label">
                <!-- dla karty pokaż: Karta (Apple Pay / Google Pay) -->
                <template v-if="(method.slug || '').toLowerCase() === 'card' || Number(method.id) === 3">
                  {{ paymentLabel(method) }} ({{ cardWalletsNote }})
                </template>
                <template v-else>
                  {{ paymentLabel(method) }}
                </template>
              </span>
            </div>
          </label>
        </div>

        <h2>{{ translations.checkout.step_terms }}</h2>
        <label class="checkbox pretty-checkbox">
          <input type="checkbox" v-model="form.terms" required />
          <span class="custom-check"></span>
          {{ translations.checkout.terms_accept }}
        </label>

        <p v-if="uiError" class="ui-error">{{ uiError }}</p>

        <button class="btn-submit" :disabled="processing">
          {{ processing ? translations.checkout.processing : translations.checkout.order_btn }}
        </button>
      </form>
    </div>
  </div>
</template>

<script>
import { translations, currentLang } from '@/locales/lang'
import { currentCurrency } from '@/composables/currency'
import { watch } from 'vue'
import { track } from '@/tracking/events'

export default {
  name: 'AppCheckout',
  data() {
    return {
      cart: [],
      availableCountries: [],
      availablePaymentMethods: [],
      processing: false,
      uiError: '',
      unwatchCurrency: null,
      form: {
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        address: '',
        city: '',
        zip: '',
        country: 'PL',
        paymentMethod: '',
        terms: false,
        wantInvoice: false,
        invoiceCompany: '',
        invoiceNIP: '',
        invoiceAddress: '',
        invoiceZip: '',
        invoiceCity: '',
        invoiceCountry: 'PL'
      }
    }
  },
  watch: {
    'form.country': {
      immediate: true,
      handler(newCountry) {
        this.fetchPaymentMethods(newCountry)
      }
    },
    'form.paymentMethod'(val) {
      if (!val || !this.cart.length) return
      const method = this.availablePaymentMethods.find(m => Number(m.id) === Number(val))
      const slug = String(method?.slug || '').toLowerCase()
      const fallback = { 1:'cod', 2:'bank_transfer', 3:'card', 4:'blik', 5:'p24', 6:'klarna', 7:'paypal' }[Number(val)] || 'other'
      const pm = slug || fallback
      const items = this.cart.map(it => ({
        id: it.id, product_id: it.id, product_name: it.name,
        variant: it.variant, sku: it.variant?.sku,
        price: Number(it.price), quantity: Number(it.quantity)
      }))
      track.addPaymentInfo({ items, currency: this.currencyCode, paymentType: pm })
    }
  },
  computed: {
    translations() { return translations.value },
    currencyCode() { return currentCurrency.value || 'PLN' },

    total() {
      return this.cart.reduce((sum, item) => sum + Number(item.price) * item.quantity, 0)
    },

    shippingTotal() {
      const key = this.priceKeyForCurrency(this.currencyCode)
      const sel = (this.form.country || '').toUpperCase()
      return this.cart.reduce((sum, item) => {
        const opts = item.variant?.shipping_options || []
        const opt = opts.find(o => String(o.country).toUpperCase() === sel)
        if (!opt) return sum
        const raw = Object.prototype.hasOwnProperty.call(opt, key) ? opt[key] : 0
        const v = Number(raw)
        return sum + (Number.isFinite(v) ? v : 0) * (item.quantity || 1)
      }, 0)
    },

    totalWithShipping() {
      return this.total + this.shippingTotal
    },

    cardWalletsNote() {
      return translations.value?.payments?.card_wallets_suffix || 'Visa / Mastercard / Apple Pay / Google Pay'
    }
  },
  async mounted() {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    window.addEventListener('languageChanged', this.reloadCartWithLang)

    this.unwatchCurrency = watch(
      () => this.currencyCode,
      () => { this.reloadCartWithLang() }
    )

    await this.prefetchIPCountry()
    const iso = this.getUserCountryISO()
    if (iso) this.form.country = iso

    await this.reloadCartWithLang()

    // GA4/Pixel: begin_checkout
    const items = this.cart.map(it => ({
      id: it.id, product_id: it.id, product_name: it.name,
      variant: it.variant, sku: it.variant?.sku,
      price: Number(it.price), quantity: Number(it.quantity)
    }))
    track.beginCheckout({
      items,
      currency: this.currencyCode,
      shipping: this.shippingTotal
    })
  },
  beforeUnmount() {
    window.removeEventListener('languageChanged', this.reloadCartWithLang)
    if (this.unwatchCurrency) this.unwatchCurrency()
  },
  methods: {

    generateEventId(type = 'purchase') {
      const ts = Date.now()
      const rand = Math.floor(Math.random() * 1e6)
      return `${type}-${ts}-${rand}`
      },

    /* ===== Waluty ===== */
    getCurrencySymbol(code) {
      const symbols = {
        PLN: 'zł', USD: '$', EUR: '€',
        CZK: 'Kč', HUF: 'Ft', BGN: 'лв',
        RON: 'lei', DKK: 'kr.', SEK: 'kr'
      }
      return symbols[code] || code
    },
    priceKeyForCurrency(code) {
      const map = {
        PLN:'price_pln', EUR:'price_eur', USD:'price_usd',
        CZK:'price_czk', HUF:'price_huf', BGN:'price_bgn',
        RON:'price_ron', DKK:'price_dkk', SEK:'price_sek'
      }
      return map[code] || 'price_pln'
    },

    /* ===== Autodetekcja kraju ===== */
    async prefetchIPCountry() {
      try {
        if (localStorage.getItem('ship_to_iso')) return
        const ctrl = new AbortController()
        const t = setTimeout(() => ctrl.abort(), 1500)
        const res = await fetch('https://ipapi.co/json/', { signal: ctrl.signal })
        clearTimeout(t)
        if (!res.ok) return
        const data = await res.json()
        const iso = data && data.country_code ? String(data.country_code).toUpperCase() : ''
        if (iso) localStorage.setItem('ship_to_iso', iso)
      } catch (e) { /* ignore */ }
    },
    getUserCountryISO() {
      const stored = localStorage.getItem('ship_to_iso')
      if (stored) return stored.toUpperCase()
      const m = (navigator.language || '').match(/-([A-Za-z]{2})$/)
      return m ? m[1].toUpperCase() : 'PL'
    },

    /* ===== Nazwy krajów ===== */
    regionToLang(code) {
      const m = {
        PL:'pl', DE:'de', AT:'de', BE:'nl', BG:'bg', HR:'hr', CZ:'cs', DK:'da', EE:'et',
        ES:'es', FR:'fr', NL:'nl', LT:'lt', LV:'lv', RO:'ro', SK:'sk', SI:'sl',
        SE:'sv', IT:'it', HU:'hu', PT:'pt', IE:'en'
      }
      return m[String(code).toUpperCase()] || 'en'
    },
    countryEndonym(iso) {
      const lang = this.regionToLang(iso)
      try {
        const dn = new Intl.DisplayNames([lang], { type: 'region' })
        return dn.of(String(iso).toUpperCase()) || String(iso).toUpperCase()
      } catch (e) {
        return String(iso).toUpperCase()
      }
    },
    countryLabel(iso, fallback) {
      const code = String(iso).toUpperCase()
      if (/^[A-Z]{2}$/.test(code)) return this.countryEndonym(code)
      return fallback || code
    },

    /* ===== Etykiety płatności ===== */
    paymentLabel(method) {
      const slug = String(method?.slug || '').toLowerCase()
      const slugToKey = {
        cod: 'cod',
        bank_transfer: 'bank_transfer',
        card: 'card',
        blik: 'blik',
        p24: 'p24',
        klarna: 'klarna',
        paypal: 'paypal',
        applepay: 'applepay',
        googlepay: 'googlepay'
      }
      const idToKey = {
        1: 'cod', 2: 'bank_transfer', 3: 'card',
        4: 'blik', 5: 'p24', 6: 'klarna', 7: 'paypal'
      }
      const key = slugToKey[slug] || idToKey[Number(method?.id)]
      const t = translations.value?.payments?.[key]
      return t || method?.name || ''
    },

    /* ===== Główne ładowanie koszyka ===== */
    async reloadCartWithLang() {
      this.uiError = ''
      let raw = []
      try { raw = JSON.parse(localStorage.getItem('cart') || '[]') } catch { raw = [] }
      const currency = this.currencyCode

      const updatedCart = await Promise.all(
        raw.map(async (entry) => {
          try {
            const res = await fetch(
              `https://birdstool.pl/api/v1/products/${entry.slug}/`,
              { headers: { 'Accept-Language': currentLang.value } }
            )
            if (!res.ok) throw new Error('API error')
            const product = await res.json()

            const variant = (product.variants || []).find(v => Number(v.id) === Number(entry.variantId))
            let price = 0

            if (variant?.prices?.length) {
              const found = variant.prices.find(p => p.currency?.code === currency)
              if (found && found.amount != null) {
                const n = Number(found.amount)
                if (Number.isFinite(n)) price = n
              }
            }
            if (!price) {
              const key = this.priceKeyForCurrency(currency)
              const fallback = product && product[key] != null ? Number(product[key]) : 0
              price = Number.isFinite(fallback) ? fallback : 0
            }

            return {
              id: product.id,
              name: product.name,
              image: product.get_image || product.get_thumbnail,
              price,
              quantity: Number(entry.quantity) || 1,
              variant
            }
          } catch { return null }
        })
      )

      this.cart = updatedCart.filter(Boolean)

      // lista krajów z opcji wysyłki
      const countriesMap = new Map()
      this.cart.forEach(item => {
        (item.variant?.shipping_options || []).forEach(opt => {
          const code = String(opt.country || '').toUpperCase()
          if (!code) return
          if (!countriesMap.has(code)) {
            const label = this.countryLabel(code, opt.country_name)
            countriesMap.set(code, label)
          }
        })
      })
      this.availableCountries = Array.from(countriesMap, ([code, name]) => ({ code, name }))
      if (!this.availableCountries.find(c => c.code === this.form.country)) {
        this.form.country = this.availableCountries[0]?.code || this.form.country
      }
    },

    async fetchPaymentMethods(country) {
      try {
        const res = await fetch(`https://birdstool.pl/api/v1/payment-methods/?country=${country}`)
        const data = await res.json()
        this.availablePaymentMethods = Array.isArray(data) ? data : []
      } catch (e) {
        console.debug('Błąd ładowania metod płatności:', e)
        this.availablePaymentMethods = []
      }
    },

    /* ===== Zamówienie + płatności ===== */
    async order() {
      if (!this.cart.length) { this.uiError = 'Koszyk jest pusty.'; return }
      this.uiError = ''
      this.processing = true
      try {
        const eventId = this.generateEventId('purchase')
        const orderPayload = {
          first_name: this.form.firstName,
          last_name: this.form.lastName,
          phone: this.form.phone,
          email: this.form.email,
          address: this.form.address,
          city: this.form.city,
          zip_code: this.form.zip,
          country: this.form.country,
          payment_method: this.form.paymentMethod,
          want_invoice: this.form.wantInvoice,
          invoice_company: this.form.invoiceCompany,
          invoice_nip: this.form.invoiceNIP,
          invoice_address: this.form.invoiceAddress,
          invoice_zip: this.form.invoiceZip,
          invoice_city: this.form.invoiceCity,
          invoice_country: this.form.invoiceCountry,
          total_price: this.total.toFixed(2),
          shipping_price: this.shippingTotal.toFixed(2),
          language: currentLang.value,
          currency: this.currencyCode,
          event_id: eventId,
          event_source_url: window.location.href,
          items: this.cart.map(item => ({
            product_id: item.id,
            product_name: item.name,
            variant_id: item.variant ? item.variant.id : null,
            variant_name: item.variant?.type?.name || '',
            price: item.price,
            quantity: item.quantity
          }))
        }

        const STRIPE_MAP = { 3: 'card', 4: 'blik', 5: 'p24', 6: 'klarna', 7: 'paypal' }

        // Bank transfer (2) — OFFLINE od razu zamykamy
        if (String(this.form.paymentMethod) === '2') {
          const response = await fetch('https://birdstool.pl/api/v1/orders/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(orderPayload)
          })
          if (!response.ok) throw new Error('Błąd zapisu zamówienia')
          const data = await response.json()

          // purchase (offline)
          const items = this.cart.map(it => ({
            id: it.id, product_id: it.id, product_name: it.name,
            variant: it.variant, sku: it.variant?.sku,
            price: Number(it.price), quantity: Number(it.quantity)
          }))
          track.purchase({
            transaction_id: data.order_number,
            items,
            currency: this.currencyCode,
            shipping: this.shippingTotal,
            tax: 0,
            event_id: eventId
          })

          localStorage.removeItem('cart'); this.cart = []
          this.$router.push({ path: '/potwierdzenie', query: { order: data.order_number } })
        }
        // Stripe — card/blik/p24/klarna/paypal
        else if (Object.prototype.hasOwnProperty.call(STRIPE_MAP, String(this.form.paymentMethod))) {
          const orderResponse = await fetch('https://birdstool.pl/api/v1/orders/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(orderPayload)
          })
          if (!orderResponse.ok) throw new Error('Błąd zapisu zamówienia')
          const orderData = await orderResponse.json()
            // 🔹 GA4 / Pixel: purchase w momencie złożenia zamówienia (Stripe/PayPal)
          const itemsForTracking = this.cart.map(it => ({
            id: it.id,
            product_id: it.id,
            product_name: it.name,
            variant: it.variant,
            sku: it.variant?.sku,
            price: Number(it.price),
            quantity: Number(it.quantity)
          }))
          track.purchase({
            transaction_id: orderData.order_number,
            items: itemsForTracking,
            currency: this.currencyCode,
            shipping: this.shippingTotal,
            tax: 0,
            event_id: eventId
          })


          const stripeResponse = await fetch('https://birdstool.pl/api/v1/stripe/create/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              order_number: orderData.order_number,
              amount: parseFloat(orderPayload.total_price),
              currency: orderPayload.currency,
              method: STRIPE_MAP[String(this.form.paymentMethod)]
            })
          })
          const stripeData = await stripeResponse.json()
          if (stripeData.url) {
            localStorage.setItem('order_number', orderData.order_number)
            window.location.href = stripeData.url
          } else {
            throw new Error(stripeData.error || 'Błąd podczas płatności Stripe')
          }
        }
        // Inne metody (np. pobranie 1)
        else {
          const response = await fetch('https://birdstool.pl/api/v1/orders/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(orderPayload)
          })
          if (!response.ok) throw new Error('Błąd zapisu zamówienia')
          const data = await response.json()

          const items = this.cart.map(it => ({
            id: it.id, product_id: it.id, product_name: it.name,
            variant: it.variant, sku: it.variant?.sku,
            price: Number(it.price), quantity: Number(it.quantity)
          }))
          track.purchase({
            transaction_id: data.order_number,
            items,
            currency: this.currencyCode,
            shipping: this.shippingTotal,
            tax: 0,
            event_id: eventId
          })

          localStorage.removeItem('cart'); this.cart = []
          this.$router.push({ path: '/potwierdzenie', query: { order: data.order_number } })
        }

      } catch (error) {
        this.uiError = 'Błąd podczas składania zamówienia.'
        console.debug(error)
      } finally {
        this.processing = false
      }
    }
  }
}
</script>

<style scoped>
.ui-error{color:#b91c1c;margin:.5rem 0;}

/* Jedna kolumna wszędzie: back-link → summary → form */
.checkout-wrapper{padding:16px;}
.checkout-container{
  max-width:1140px;
  margin:0 auto;
  display:flex;
  flex-direction:column;
  gap:1rem;
}

/* back-link na górze, pod nim karta Podsumowanie */
.back-link{margin:0 0 8px 0;}
.summary-section{
  order:0;                 /* tuż po back-linku */
  background:#fff;
  border:1px solid #e5e7eb;
  border-radius:12px;
  padding:14px 16px;
  margin-top:0;            /* przyklejone pod back-linkiem */
}
.form-section{ order:1; }  /* formularz po podsumowaniu */

/* (usuwamy sticky i grid na desktopie) */

/* Zawartość karty podsumowania */
.summary-section h3{margin:0 0 .75rem;}
.summary-row{display:flex;justify-content:space-between;margin:.25rem 0;}
.summary-note{color:#6b7280;font-size:.95rem;}
.summary-total{display:flex;justify-content:space-between;margin-top:.6rem;}
.cart-item{display:flex;gap:.75rem;padding:.25rem 0;}
.cart-img-container{width:56px;height:56px;border:1px solid #eee;border-radius:8px;overflow:hidden;flex:0 0 56px;}
.cart-img{width:100%;height:100%;object-fit:cover}

/* Gridy pól formularza */
.two-cols{display:grid;grid-template-columns:1fr;gap:.75rem;}
@media (min-width:720px){ .two-cols{grid-template-columns:1fr 1fr;} }

/* płatności */
.payment-box-group{display:flex;flex-direction:column;gap:.5rem;}
.payment-box{
  display:flex;align-items:center;justify-content:space-between;
  border:1px solid #e5e7eb;border-radius:8px;padding:.75rem 1rem;
  transition:border-color .2s,background .2s;cursor:pointer;background:#fafafa;
}
.payment-box.selected{border-color:#2563eb;background:#f0f5ff;}
.radio-content{display:flex;align-items:center;justify-content:flex-start;gap:.5rem;width:100%;}
.method-label{display:flex;align-items:center;gap:.3rem;font-weight:500;}
.method-note{font-size:.85rem;opacity:.8;}
</style>

